/**
 * Fiche produit — enrichissements progressifs (Web Components natifs, zéro dépendance).
 *
 * Tout fonctionne sans JavaScript (galerie en scroll-snap + ancres, sélecteur de
 * repli <noscript>, quantité en <input type=number>, formulaire soumis nativement).
 * Ce script ne fait qu'améliorer : synchronisation des vignettes, zoom, résolution
 * de variante, stepper de quantité, barre d'achat collante.
 *
 * L'ajout au panier lui-même est géré par l'intercepteur global de cart-items.js.
 */

/* ------------------------------------------------------------------ */
/* Galerie média                                                       */
/* ------------------------------------------------------------------ */
class ProductGallery extends HTMLElement {
  connectedCallback() {
    this.track = this.querySelector('[data-gallery-track]');
    this.slides = Array.from(this.querySelectorAll('[data-gallery-slide]'));
    this.thumbs = Array.from(this.querySelectorAll('[data-gallery-thumb]'));
    this.dialog = this.querySelector('[data-gallery-dialog]');
    this.dialogImage = this.querySelector('[data-gallery-dialog-image]');

    // Vignette cliquée : défile la galerie (et non la page entière).
    this.thumbs.forEach((thumb, index) => {
      thumb.addEventListener('click', (event) => {
        event.preventDefault();
        this.scrollToIndex(index);
        thumb.focus({ preventScroll: true });
      });
    });

    // Vignette active suivant la diapo visible.
    if (this.track && this.slides.length > 1 && 'IntersectionObserver' in window) {
      this.observer = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (entry.isIntersecting) {
              const index = this.slides.indexOf(entry.target);
              this.setActiveThumb(index);
            }
          });
        },
        { root: this.track, threshold: 0.6 }
      );
      this.slides.forEach((slide) => this.observer.observe(slide));
    }

    // Zoom dans le <dialog>.
    this.querySelectorAll('[data-gallery-zoom]').forEach((btn) => {
      btn.addEventListener('click', () => this.openZoom(btn));
    });
    this.dialog?.querySelector('[data-gallery-dialog-close]')?.addEventListener('click', () => this.dialog.close());
    this.dialog?.addEventListener('click', (event) => {
      if (event.target === this.dialog) this.dialog.close();
    });
  }

  scrollToIndex(index) {
    const slide = this.slides[index];
    if (!slide || !this.track) return;
    this.track.scrollTo({ left: slide.offsetLeft - this.track.offsetLeft, behavior: 'smooth' });
    this.setActiveThumb(index);
  }

  setActiveThumb(index) {
    this.thumbs.forEach((thumb, i) => thumb.classList.toggle('is-active', i === index));
  }

  openZoom(btn) {
    if (!this.dialog || !this.dialogImage) return;
    const src = btn.dataset.zoomSrc;
    const img = btn.querySelector('img');
    if (src) this.dialogImage.src = src;
    this.dialogImage.alt = img ? img.alt : '';
    this.dialog.showModal();
  }
}
if (!customElements.get('product-gallery')) {
  customElements.define('product-gallery', ProductGallery);
}

/* ------------------------------------------------------------------ */
/* Barre d'achat collante (mobile + desktop)                           */
/* ------------------------------------------------------------------ */
class ProductStickyBar extends HTMLElement {
  connectedCallback() {
    const scope = this.closest('.shopify-section') || document;
    this.anchor = scope.querySelector('[data-product-buy] [data-add-to-cart]');
    if (!this.anchor) return;

    // Fiable : on évalue la position du bouton d'ajout principal à CHAQUE
    // défilement, directement (pas de requestAnimationFrame). Lecture live à chaque
    // scroll — robuste aux décalages de mise en page (images qui chargent, blocs
    // qui s'ouvrent au-dessus du bouton).
    //
    // RÈGLE UNIQUE, quelle que soit la taille d'écran : la barre est visible SAUF
    // quand le bouton d'ajout principal est réellement à l'écran (donc cliquable).
    // Elle apparaît dès l'arrivée sur la page si le bouton est sous le pli,
    // disparaît quand on l'atteint, réapparaît quand il est passé.
    // Demande marchand 2026-08-15 (mobile), étendue au desktop le 2026-08-16.
    //
    // POURQUOI l'ancienne exception desktop a été retirée (audit CRO F1) : elle
    // masquait la barre tant que le bouton n'était pas ENTIÈREMENT remonté au-dessus
    // de l'écran (`hidden = rect.bottom > 0`). Ce raisonnement suppose que le bouton
    // est visible au chargement — vrai en 1920×1080 (bouton à y=949 pour un pli à
    // 1080), FAUX en 1440×900, le format de portable le plus courant : le bouton y
    // est à y=1059, soit 159 px SOUS le pli. La règle masquait donc la barre
    // exactement dans le cas où elle est utile, et le défaut était invisible depuis
    // un grand écran — celui sur lequel on conçoit le site.
    //
    // La règle unifiée est auto-limitante : quand le bouton principal EST visible
    // au chargement (grands écrans), la barre reste masquée comme avant. Rien ne
    // change en 1920 ; seuls les écrans où le bouton démarre sous le pli gagnent le
    // filet qui existait déjà sur mobile.
    //
    // Volontairement SANS rAF : throttler une seule lecture getBoundingClientRect
    // + un toggle d'attribut sur un unique élément n'apporte rien, et le latch de
    // throttling pouvait se coincer (drapeau « occupé » figé au chargement).
    this._update = () => {
      const rect = this.anchor.getBoundingClientRect();
      const visible = rect.bottom > 0 && rect.top < window.innerHeight;
      this.toggleAttribute('hidden', visible);
    };
    window.addEventListener('scroll', this._update, { passive: true });
    window.addEventListener('resize', this._update, { passive: true });
    this._update();
  }

  disconnectedCallback() {
    if (!this._update) return;
    window.removeEventListener('scroll', this._update);
    window.removeEventListener('resize', this._update);
  }
}
if (!customElements.get('product-sticky-bar')) {
  customElements.define('product-sticky-bar', ProductStickyBar);
}

/* ------------------------------------------------------------------ */
/* Quantité (stepper) + résolution de variante                         */
/* ------------------------------------------------------------------ */
function initQuantitySteppers(scope) {
  scope.querySelectorAll('[data-quantity-stepper]').forEach((stepper) => {
    const input = stepper.querySelector('[data-qty-input]');
    if (!input) return;
    stepper.querySelector('[data-qty-minus]')?.addEventListener('click', () => {
      const min = parseInt(input.min, 10) || 1;
      input.value = Math.max(min, (parseInt(input.value, 10) || min) - 1);
      input.dispatchEvent(new Event('change', { bubbles: true }));
    });
    stepper.querySelector('[data-qty-plus]')?.addEventListener('click', () => {
      input.value = (parseInt(input.value, 10) || 0) + 1;
      input.dispatchEvent(new Event('change', { bubbles: true }));
    });
  });
}

function initVariantPicker(root) {
  const dataEl = root.querySelector('[data-variants]');
  const picker = root.querySelector('[data-variant-picker]');
  if (!dataEl || !picker) return;

  let variants;
  try {
    variants = JSON.parse(dataEl.textContent);
  } catch (error) {
    console.warn('Fiche produit : données de variantes illisibles', error);
    return;
  }

  const scope = root.closest('.shopify-section') || document;
  const form = root.querySelector('form.product-form');
  const selects = Array.from(picker.querySelectorAll('[data-option-index]'));
  if (!form || selects.length === 0) return;

  // Champ caché `id` maintenu par le JS (le serveur ne le rend pas en multi-variante).
  let idInput = form.querySelector('[data-variant-id]');
  if (!idInput) {
    idInput = document.createElement('input');
    idInput.type = 'hidden';
    idInput.name = 'id';
    idInput.setAttribute('data-variant-id', '');
    form.prepend(idInput);
  }

  const gallery = scope.querySelector('product-gallery');

  function resolve() {
    const chosen = selects.map((s) => s.value);
    const variant = variants.find((v) => v.options.length === chosen.length && v.options.every((opt, i) => opt === chosen[i]));

    if (!variant) return;
    idInput.value = variant.id;

    scope.querySelectorAll('[data-price]').forEach((el) => { el.textContent = variant.price; });
    scope.querySelectorAll('[data-compare-price]').forEach((el) => {
      el.textContent = variant.compare_at || '';
      el.toggleAttribute('hidden', !variant.compare_at);
    });
    // Mention prix de référence (Omnibus) : visible seulement en promotion.
    scope.querySelectorAll('[data-price-reference]').forEach((el) => {
      el.toggleAttribute('hidden', !variant.compare_at);
    });
    scope.querySelectorAll('[data-unit-price]').forEach((el) => {
      el.textContent = variant.unit || '';
      el.toggleAttribute('hidden', !variant.unit);
    });

    scope.querySelectorAll('[data-add-to-cart]').forEach((btn) => {
      btn.disabled = !variant.available;
      const label = btn.querySelector('[data-add-to-cart-text]') || btn;
      label.textContent = variant.available ? btn.dataset.labelAdd : btn.dataset.labelSoldOut;
    });

    // Indicateur de stock réel (§7.4) : même logique honnête que le rendu serveur
    // (product-stock-level.liquid). Affiché seulement si suivi actif, vente arrêtée
    // à zéro, variante disponible et quantité basse. Jamais de rareté fictive (§12.2).
    scope.querySelectorAll('[data-stock-level]').forEach((el) => {
      const inv = variant.inventory;
      const threshold = parseInt(el.dataset.stockThreshold, 10) || 0;
      const show = !!inv && inv.tracked && inv.policy_deny && variant.available && inv.qty > 0 && inv.qty <= threshold;
      if (show) {
        const textEl = el.querySelector('[data-stock-text]');
        if (textEl) textEl.textContent = (el.dataset.stockTemplate || '').replace('[qty]', inv.qty);
      }
      el.toggleAttribute('hidden', !show);
    });

    if (variant.media && gallery) gallery.scrollToIndex(variant.media - 1);

    const url = new URL(window.location.href);
    url.searchParams.set('variant', variant.id);
    window.history.replaceState({}, '', url);
  }

  selects.forEach((select) => select.addEventListener('change', resolve));
}

/* ------------------------------------------------------------------ */
/* Produits récemment consultés (P2, §7.4) — écriture localStorage      */
/* ------------------------------------------------------------------ */
const RECENTLY_VIEWED_KEY = 'boteh:recently-viewed';
const RECENTLY_VIEWED_MAX = 12;

/** Enregistre la fiche produit courante dans l'historique local du visiteur.
 *  Aucune donnée envoyée à un serveur. Lu par assets/recently-viewed.js. */
function recordRecentlyViewed(root) {
  const dataEl = root.querySelector('[data-recently-viewed-record]');
  if (!dataEl) return;

  let entry;
  try {
    entry = JSON.parse(dataEl.textContent);
  } catch (error) {
    return;
  }
  if (!entry || typeof entry.handle !== 'string' || !entry.handle) return;

  let list;
  try {
    list = JSON.parse(localStorage.getItem(RECENTLY_VIEWED_KEY) || '[]');
  } catch (error) {
    list = [];
  }
  if (!Array.isArray(list)) list = [];

  list = list.filter((item) => item && item.handle !== entry.handle);
  list.unshift(entry);
  list = list.slice(0, RECENTLY_VIEWED_MAX);

  try {
    localStorage.setItem(RECENTLY_VIEWED_KEY, JSON.stringify(list));
  } catch (error) {
    // Stockage indisponible (navigation privée, quota atteint) : dégradation silencieuse.
  }
}

/* ------------------------------------------------------------------ */
/* Achat unique vs abonnement (P1, §7.4)                                */
/* ------------------------------------------------------------------ */
/**
 * Le <select name="selling_plan"> se suffit à lui-même (soumission native,
 * fonctionne sans JavaScript). Cette fonction n'ajoute qu'un aperçu de prix
 * qui se met à jour au choix — pure amélioration, jamais requise pour acheter.
 */
function initSubscriptionToggle(root) {
  const select = root.querySelector('[data-selling-plan-select]');
  if (!select) return; // Aucun plan de vente configuré sur ce produit.

  const scope = root.closest('.shopify-section') || document;

  function applyPrice() {
    const option = select.options[select.selectedIndex];
    if (!option) return;
    scope.querySelectorAll('[data-price]').forEach((el) => { el.textContent = option.dataset.price || ''; });
    scope.querySelectorAll('[data-compare-price]').forEach((el) => {
      const compare = option.dataset.comparePrice || '';
      el.textContent = compare;
      el.toggleAttribute('hidden', !compare);
    });
    scope.querySelectorAll('[data-price-reference]').forEach((el) => {
      el.toggleAttribute('hidden', !option.dataset.comparePrice);
    });
  }

  select.addEventListener('change', applyPrice);
}

/* ------------------------------------------------------------------ */
/* Barre de paliers : synchro LIVE sur le total du panier               */
/* ------------------------------------------------------------------ */
/**
 * Sur la fiche, la barre « plus vous en prenez… » est rendue côté serveur avec
 * `cart.item_count` UNE fois au chargement. Quand le panier change ensuite (ajout
 * depuis la fiche, +/- dans le tiroir), le tiroir se re-rend mais pas cette barre :
 * elle resterait figée → incohérence avec le panier (peur d'un bug → perte de
 * confiance). On la resynchronise donc en direct sur le TOTAL réel du panier à
 * chaque `cart:updated` (émis par cart-items.js). Le total de vérité est lu sur
 * `[data-cart-contents]` (re-rendu AVANT l'événement), comme le compteur du header.
 *
 * Algorithme d'affichage IDENTIQUE à composer-assortiment.js `updateVolumeBar`
 * (même source de paliers, mêmes gabarits de texte) → cohérence garantie. On ne
 * cible QUE la barre marquée `data-volume-cart-sync` (la fiche) : ni le tiroir
 * (déjà re-rendu), ni le composer (qui simule une sélection non encore au panier).
 */
function initVolumeBarCartSync() {
  const bar = document.querySelector('[data-volume-bar][data-volume-cart-sync]');
  if (!bar) return;

  let tiers;
  try {
    tiers = JSON.parse(bar.dataset.volumeTiers || '[]');
  } catch (error) {
    return;
  }
  if (!Array.isArray(tiers) || tiers.length === 0) return;

  const statusEl = bar.querySelector('[data-volume-status]');
  const fillEl = bar.querySelector('[data-volume-fill]');
  const trackEl = bar.querySelector('[data-volume-track]');
  const chips = bar.querySelectorAll('.volume-bar__tier');
  const tplOne = bar.dataset.tplOne || '';
  const tplOther = bar.dataset.tplOther || '';
  const tplMax = bar.dataset.tplMax || '';
  const tplReached = bar.dataset.tplReached || '';

  function update(qty) {
    let reachedLabel = '';
    let next = null;
    tiers.forEach((tier) => {
      if (tier.qty <= qty) reachedLabel = tier.label;
      if (tier.qty > qty && !next) next = tier;
    });

    if (statusEl) {
      if (next) {
        const remaining = next.qty - qty;
        const tpl = remaining === 1 ? tplOne : tplOther;
        const nextText = tpl.replace('%count%', remaining).replace('%label%', next.label);
        statusEl.textContent =
          reachedLabel && tplReached
            ? `${tplReached.replace('%label%', reachedLabel)} · ${nextText}`
            : nextText;
      } else {
        statusEl.textContent = tplMax.replace('%label%', reachedLabel);
      }
    }

    const progress = next ? Math.min(100, Math.round((qty / next.qty) * 100)) : 100;
    if (fillEl) fillEl.style.width = `${progress}%`;
    trackEl?.setAttribute('aria-valuenow', String(progress));

    chips.forEach((chip) => {
      const tierQty = parseInt(chip.dataset.tierQty, 10) || 0;
      chip.classList.toggle('is-reached', tierQty <= qty);
      chip.classList.toggle('is-next', next != null && tierQty === next.qty);
    });
  }

  function liveCartCount() {
    const el = document.querySelector('[data-cart-contents]');
    return el ? parseInt(el.dataset.cartCount, 10) || 0 : 0;
  }

  document.addEventListener('cart:updated', () => update(liveCartCount()));
}

/* ------------------------------------------------------------------ */
/* Provenance : cette fiche a-t-elle été atteinte depuis le quiz ?      */
/* ------------------------------------------------------------------ */
/**
 * Pendant du marquage posé en Liquid sur les cartes du conseil
 * (sections/quiz-recommandation.liquid). On regarde presque toujours la fiche
 * avant d'acheter : sans ce relais, ces ventes échapperaient à la mesure et le
 * quiz semblerait ne rien vendre.
 *
 * La mémoire est écrite par assets/quiz-recommandation.js au clic sur une carte
 * du conseil, en sessionStorage — donc valable pour la VISITE en cours
 * seulement, et uniquement pour les produits venus du quiz. Un produit trouvé
 * par un autre chemin n'est jamais attribué au quiz.
 *
 * Propriété de ligne native, aucune app, aucun pixel : elle est filtrée à
 * l'affichage par snippets/cart-line.liquid (préfixe `_`), donc invisible du
 * client, et remonte sur la commande (API Admin `LineItem.customAttributes`).
 */
const QUIZ_SOURCE_KEY = 'boteh:quiz-src';

function tagQuizProvenance(root) {
  const form = root.querySelector('form.product-form');
  if (!form || form.querySelector('[data-quiz-provenance]')) return;

  const handle = window.location.pathname.split('/products/')[1]?.split('/')[0];
  if (!handle) return;

  let label;
  try {
    const map = JSON.parse(sessionStorage.getItem(QUIZ_SOURCE_KEY) || '{}');
    label = map && typeof map === 'object' ? map[handle] : null;
  } catch (error) {
    return; // stockage indisponible : on perd la mesure, jamais la vente
  }
  if (!label) return;

  const input = document.createElement('input');
  input.type = 'hidden';
  input.name = 'properties[_quiz]';
  input.value = label;
  input.setAttribute('data-quiz-provenance', '');
  form.appendChild(input);
}

document.querySelectorAll('[data-product-root]').forEach((root) => {
  initQuantitySteppers(root);
  recordRecentlyViewed(root);
  initVariantPicker(root);
  initSubscriptionToggle(root);
  tagQuizProvenance(root);
});

initVolumeBarCartSync();
